import { TimesheetStatus } from 'components/organisms/timesheets/types';
import { TimesheetEntryType, TimesheetUISourceType } from '..';
import {
  ApiReport,
  ApiReportCategoryInfo,
  ApiReportCategoryRecord,
  ApiReportPhaseRecord,
  ApiReportProjectRecord,
  ApiReportRecord,
  ApiReportTaskRecord,
  ApiReportUserRecord,
} from './api-types';
import {
  TimesheetReportCategorySummary,
  TimesheetReportSummary,
  OtherCategorySummary,
  NonCategorySummary,
  TimesheetCategoryWiseReportSummary,
  TimesheetProjectWiseReportSummary,
  TimesheetUserWiseReportSummary,
  CategorySummaryType,
  TimesheetProjectReport,
  TimesheetUserReport,
  TimesheetCategoryReport,
  TimesheetReportType,
  TimesheetTaskReport,
  TimesheetPhaseReport,
} from './types';

const getCategoryInfoFromApi = (
  apiCategory: ApiReportCategoryInfo,
): TimesheetReportCategorySummary[] => {
  const categories: TimesheetReportCategorySummary[] = [];

  categories.push(
    ...apiCategory.top.map((_category) => ({
      type: CategorySummaryType.Top,
      totalMinutes: _category.totalMinutes,
      categoryId: _category.categoryId,
    })),
  );
  if (apiCategory.other.totalMinutes) {
    categories.push({
      type: CategorySummaryType.Other,
      totalMinutes: apiCategory.other.totalMinutes,
    } as OtherCategorySummary);
  }
  if (apiCategory.none.totalMinutes) {
    categories.push({
      type: CategorySummaryType.None,
      totalMinutes: apiCategory.none.totalMinutes,
    } as NonCategorySummary);
  }

  return categories;
};

export const asSummary = ({
  totalBillableMinutes: billableMinutes,
  totalNonBillableMinutes: nonBillableMinutes,
  individualCapacity,
  report,
  category,
}: ApiReport<ApiReportUserRecord>): TimesheetReportSummary => ({
  billableMinutes,
  nonBillableMinutes,
  capacityInMinutes: individualCapacity * 60,
  numberOfUniqueUser: report?.length,
  categories: getCategoryInfoFromApi(category),
  users: report?.map(({ user }) => user),
});

export const asProjectWiseSummary = ({
  totalBillableMinutes: billableMinutes,
  totalNonBillableMinutes: nonBillableMinutes,
  teamCapacity,
  category,
  totalAllocatedMinutes,
}: ApiReport<ApiReportRecord>): TimesheetProjectWiseReportSummary => ({
  billableMinutes,
  nonBillableMinutes,
  capacityInMinutes: teamCapacity * 60,
  categories: getCategoryInfoFromApi(category),
  totalAllocatedMinutes,
});

export const asUserWiseSummary = ({
  totalBillableMinutes: billableMinutes,
  totalNonBillableMinutes: nonBillableMinutes,
  individualCapacity,
  category,
  totalAllocatedMinutes,
}: ApiReport<ApiReportRecord>): TimesheetUserWiseReportSummary => ({
  billableMinutes,
  nonBillableMinutes,
  capacityInMinutes: individualCapacity * 60,
  numberOfUniqueUser: 1,
  categories: getCategoryInfoFromApi(category),
  totalAllocatedMinutes,
});

export const asCategoryWiseSummary = ({
  totalBillableMinutes: billableMinutes,
  totalNonBillableMinutes: nonBillableMinutes,
}: ApiReport<ApiReportRecord>): TimesheetCategoryWiseReportSummary => ({
  billableMinutes,
  nonBillableMinutes,
});

export const asProjectReport = ({
  report: records,
}: ApiReport<ApiReportProjectRecord>): TimesheetProjectReport[] =>
  (records ?? []).map(
    ({
      categoryGroup: categories,
      project,
      info: { billableMinutes, nonBillableMinutes },
      allocationInfo: { minutes },
    }) => ({
      type: TimesheetReportType.Project,
      project,
      billableMinutes,
      nonBillableMinutes,
      categories,
      allocatedMinutes: minutes,
    }),
  );

export const asUserReport = ({
  report: records,
}: ApiReport<ApiReportUserRecord>): TimesheetUserReport[] => {
  return (records ?? []).map(
    ({
      categoryGroup: categories,
      user,
      info: { billableMinutes, nonBillableMinutes },
      allocationInfo: { minutes },
      timeOffInfo,
    }) => ({
      type: TimesheetReportType.User,
      user,
      billableMinutes,
      nonBillableMinutes,
      categories,
      allocatedMinutes: minutes,
      timeOffMinutes: timeOffInfo == null ? 0 : timeOffInfo.timeOffMinutes,
    }),
  );
};

const asTimesheetTask = (record: ApiReportTaskRecord) =>
  record.entryType === TimesheetEntryType.TaskEntry
    ? {
        ...record.task,
        entryType: record.entryType,
        sourceType: TimesheetUISourceType.Task,
        sourceId: record.task.id,
        approvalStatus: TimesheetStatus.NotSubmitted,
      }
    : {
        activityName: record.activityName,
        entryType: record.entryType,
        sourceType: TimesheetUISourceType.Adhoc,
        approvalStatus: TimesheetStatus.NotSubmitted,
      };

export const asTaskReport = ({
  report: records,
}: ApiReport<ApiReportTaskRecord>): TimesheetTaskReport[] => {
  return (records ?? []).map((x) => {
    const {
      categoryGroup: categories,
      info: { billableMinutes, nonBillableMinutes },
    } = x;

    return {
      type: TimesheetReportType.Task,
      task: asTimesheetTask(x),
      billableMinutes,
      nonBillableMinutes,
      categories,
    };
  });
};

export const asPhaseReport = ({
  report: records,
}: ApiReport<ApiReportPhaseRecord>): TimesheetPhaseReport[] => {
  return (records ?? []).map((x) => {
    const {
      categoryGroup: categories,
      info: { billableMinutes, nonBillableMinutes },
      projectPhase,
    } = x;

    return {
      type: TimesheetReportType.Phase,
      projectPhase,
      billableMinutes,
      nonBillableMinutes,
      categories,
    };
  });
};

export const asCategoryReport = ({
  report: records,
}: ApiReport<ApiReportCategoryRecord>): TimesheetCategoryReport[] => {
  return (records ?? []).map(
    ({ category, info: { billableMinutes, nonBillableMinutes } }) => ({
      type: TimesheetReportType.Category,
      category,
      billableMinutes,
      nonBillableMinutes,
    }),
  );
};
